import {
  ColumnDataTypes,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  BROWSER_AI_TOOLS_FILTERABLE_FIELDS,
  BROWSER_AI_TOOLS_SORTABLE_FIELDS,
  BROWSER_AI_TOOLS_TREND_OPTIONS,
  type BrowserAiToolListItem,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import {
  StringOperators as BaseStringOperators,
  EnumOperators as BaseEnumOperators,
  DateOperators as BaseDateOperators,
  EmptyOperators,
  BROWSER_TOOLS_LAST_SEEN_TOOLTIP,
} from '@/components/shared/shadow-ai';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  DomainCell,
  NameCell,
  CategoryCell,
  TrendCell,
  FirstDetectedCell,
  LastSeenCell,
} from '@/components/shared/shadow-ai/BrowserToolCells';
import {
  DomainFilterOption,
  CategoryFilterOption,
  TrendFilterOption,
} from '@/components/shared/shadow-ai/BrowserToolFilterComponents';
import { PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS } from './constants';
import { fetchPersonalBrowserToolsFilterOptions } from './api';
import { TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER } from '@/components/shared/timestampColumnWidth';

const StringOperators = [...BaseStringOperators, ...EmptyOperators];
const EnumOperators = [...BaseEnumOperators, ...EmptyOperators];
const DateOperators = [...BaseDateOperators, ...EmptyOperators];

export function createPersonalBrowserToolsColumns(): ColumnConfig<BrowserAiToolListItem>[] {
  return [
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.DOMAIN,
      header: 'Domain',
      accessor: 'domain',
      renderCell: ({ row }) => <DomainCell row={row} />,
      width: 220,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.DOMAIN,
      sortKey: 'dom',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.DOMAIN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.personalBrowserAiToolsFilterOptions(
            'domain',
            params
          ),
        queryFn: params =>
          fetchPersonalBrowserToolsFilterOptions('domain', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <DomainFilterOption domain={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) => <NameCell row={row} />,
      width: 200,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.NAME,
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.NAME
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.personalBrowserAiToolsFilterOptions(
            'name',
            params
          ),
        queryFn: params =>
          fetchPersonalBrowserToolsFilterOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.CATEGORY,
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.CATEGORY,
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.CATEGORY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.personalBrowserAiToolsFilterOptions(
            'category',
            params
          ),
        queryFn: params =>
          fetchPersonalBrowserToolsFilterOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <CategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND,
      header: 'Trend',
      accessor: 'trend',
      renderCell: ({ row }) => <TrendCell row={row} />,
      width: 130,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.TREND,
      sortKey: 'tr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [...BROWSER_AI_TOOLS_TREND_OPTIONS],
        render: (option, isSelected) => (
          <TrendFilterOption trend={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED,
      header: 'First Detected',
      timestamp: true,
      minWidth: TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER,
      accessor: 'firstDetected',
      renderCell: ({ row }) => <FirstDetectedCell row={row} />,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.FIRST_DETECTED,
      sortKey: 'fd',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN,
      header: 'Last Seen',
      timestamp: true,
      description: BROWSER_TOOLS_LAST_SEEN_TOOLTIP,
      accessor: 'lastSeen',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: BROWSER_AI_TOOLS_SORTABLE_FIELDS.LAST_SEEN,
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          PERSONAL_BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
